// @ts-check
import { getCasePropSelector } from '../utils/case-prop-selector'
import { ALDRETE_FIELDS, PADS_FIELDS } from '../utils/discharge-score'
import { isNumber } from '../utils/is'
import { pluck } from '../utils/object'
import { createSelector } from '../utils/selector-utils'
import { getVitalTypesById } from '../vital-types'
import { optionSelectCombinedAutoAndManualCheckpointsWithinCaseTime } from './manual-vitals'
import {
  BASIC_VITALS_IDS,
  optionSelectFirstInCaseVitalsRaw,
  optionSelectLastInCaseVitalsRaw,
} from './numeric-vitals'

const aldreteVitalsKeys = ['sbp', 'dbp']
const padsVitalsKeys = ['sbp', 'dbp', 'map', 'spo2', 'hr', 'rr', 'etco2']

/** @type {CaseSelector<DischargeScore>} */
export const selectDischargeScoreType = getCasePropSelector(
  'dischargeScoreType',
  'aldrete'
)

/**
 * @type {CaseSelector<
 *   ['sbp', 'dbp'] | ['sbp', 'dbp', 'map', 'spo2', 'hr', 'rr', 'etco2']
 * >}
 */
export const selectVitalTypeKeysForDischargeScore = createSelector(
  selectDischargeScoreType,
  type => (type === 'pads' ? padsVitalsKeys : aldreteVitalsKeys)
)

/** @type {CaseSelector<CaseData['dischargeScoreEntries']>} */
const selectDischargeScoreEntries = getCasePropSelector(
  'dischargeScoreEntries',
  {}
)

/**
 * @param {string} field
 * @param {number} score
 * @returns {Record<string, number>}
 */
export const actionSetDischargeScoreEntry = (field, score) => ({
  [`dischargeScoreEntries.${field}.score`]: score,
})

/** @type {CaseSelector<DischargeQuestionEntry[]>} */
export const selectDischargeScoreFields = createSelector(
  selectDischargeScoreEntries,
  selectDischargeScoreType,
  /**
   * @param {CaseData['dischargeScoreEntries']} entries
   * @param {DischargeScore} type
   * @returns {DischargeQuestionEntry[]}
   */
  (entries, type) =>
    (type === 'pads' ? PADS_FIELDS : ALDRETE_FIELDS).map(field => {
      const foundEntry = entries[field.name]
      const score =
        foundEntry && isNumber(foundEntry.score) ? foundEntry.score : null
      const selectedOption = field.options.find(option => option[1] === score)
      /** @type {DischargeQuestionEntry} */
      const toReturn = {
        ...field,
        value: selectedOption ? selectedOption[0] : null,
        score,
      }
      return toReturn
    })
)

const getOrderedNumericVitalsFn = (last = false) => /**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'tempUnit' | 'maxDuration'
 * >} opts
 * @returns {NumericVitalValues}
 */ (caseData, { appTime, tempUnit, maxDuration }) =>
  pluck(
    (last ? optionSelectLastInCaseVitalsRaw : optionSelectFirstInCaseVitalsRaw)(
      caseData,
      {
        appTime,
        tempUnit,
        maxDuration,
      }
    ),
    selectVitalTypeKeysForDischargeScore(caseData)
  )

export const optionSelectFirstInCaseVitalsToCompare = getOrderedNumericVitalsFn(
  false
)
export const optionSelectLastInCaseVitalsToCompare = getOrderedNumericVitalsFn(
  true
)

/**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'tempUnit' | 'maxDuration'
 * >} opts
 * @returns {DischargeVitalsComparisonEntry[] | null}
 */
export const optionSelectVitalsForDischargeComparison = (
  caseData,
  { appTime, tempUnit, maxDuration }
) => {
  const opts = { appTime, tempUnit, maxDuration }
  const checkpoints = optionSelectCombinedAutoAndManualCheckpointsWithinCaseTime(
    caseData,
    opts
  )
  if (checkpoints.length < 2) {
    return null
  }
  const nameLookup = getVitalTypesById({ tempUnit })
  const first = optionSelectFirstInCaseVitalsToCompare(caseData, opts)
  const last = optionSelectLastInCaseVitalsToCompare(caseData, opts)
  /** @type {DischargeVitalsComparisonEntry[]} */
  const result = []
  for (const key in first) {
    const startValue = first[key]
    const endValue = last[key]
    const percentageDelta = Math.round(
      ((endValue - startValue) / startValue) * 100
    )
    if (!isNaN(percentageDelta)) {
      const vital = nameLookup[key]
      const name = (vital && vital.name) || key
      result.push({
        id: key,
        name,
        startValue,
        endValue,
        percentageDelta,
      })
    }
  }
  return result.length ? result : null
}

/**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'tempUnit' | 'maxDuration'
 * >} opts
 * @returns {{ start: NumericVitalValues; end: NumericVitalValues }}
 */
export const optionSelectStartEndBasicVitals = (
  caseData,
  { appTime, tempUnit, maxDuration }
) => ({
  start: pluck(
    optionSelectFirstInCaseVitalsRaw(caseData, {
      appTime,
      tempUnit,
      maxDuration,
    }),
    BASIC_VITALS_IDS
  ),
  end: pluck(
    optionSelectLastInCaseVitalsRaw(caseData, {
      appTime,
      tempUnit,
      maxDuration,
    }),
    BASIC_VITALS_IDS
  ),
})

/**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'tempUnit' | 'maxDuration'
 * >} param1
 * @returns {{
 *   maxDelta: number
 *   vitalsScore: number
 *   vitalsComparison: DischargeVitalsComparisonEntry[]
 * } | null}
 *   }
 */
export const optionSelectDischargeVitalsScoreData = (
  caseData,
  { appTime, tempUnit, maxDuration }
) => {
  const forComparison = optionSelectVitalsForDischargeComparison(caseData, {
    appTime,
    tempUnit,
    maxDuration,
  })
  if (!forComparison || forComparison.length <= 1) {
    return null
  }
  const values = forComparison.map(item => Math.abs(item.percentageDelta))
  const maxDelta = Math.max(...values, 0)

  if (!isNumber(maxDelta)) {
    return null
  }

  let vitalsScore = 0

  if (maxDelta < 20) {
    vitalsScore = 2
  } else if (maxDelta < 40) {
    vitalsScore = 1
  }

  return {
    maxDelta,
    vitalsScore,
    vitalsComparison: forComparison,
  }
}

const severityMap = ['severe', 'moderate', 'minimal']
/**
 * Pads does a weird thing where it combines the score of two fields for several
 * of the fields. This function calculates the total score for each field and
 * then combines them into a summary.
 *
 * @param {DischargeQuestionEntry[]} fields
 * @returns {DischargeScoreValue[]}
 */
const getPadsSummaryValues = fields => {
  /** @type {Record<string, DischargeQuestionEntry>} */
  const fieldsByName = fields.reduce((acc, field) => {
    acc[field.name] = field
    return acc
  }, {})

  // based on the field names we know exist in the PADS_FIELDS array
  const {
    steadyGait,
    oriented,
    voided,
    poFluids,
    surgicalBleeding,
    painNauseaVomiting,
  } = fieldsByName

  let mentalStatus = ''
  if (isNumber(oriented.score)) {
    mentalStatus = oriented.score ? 'oriented' : 'not oriented'
  }
  if (isNumber(steadyGait.score)) {
    if (mentalStatus) {
      mentalStatus += ', '
    }
    mentalStatus += steadyGait.score ? 'steady gait' : 'not steady gait'
  }

  let intake = ''
  if (isNumber(poFluids.score)) {
    intake = poFluids.score ? 'PO fluids' : 'no PO fluids'
  }
  if (isNumber(voided.score)) {
    if (intake) {
      intake += ', '
    }
    intake += voided.score ? 'voided' : 'not voided'
  }

  return [
    [
      'Activity/mental status',
      mentalStatus,
      isNumber(oriented.score) || isNumber(steadyGait.score)
        ? (oriented.score || 0) + (steadyGait.score || 0)
        : null,
    ],
    [
      painNauseaVomiting.label,
      severityMap[painNauseaVomiting.score],
      isNumber(painNauseaVomiting.score) ? painNauseaVomiting.score : null,
    ],
    [
      surgicalBleeding.label,
      severityMap[surgicalBleeding.score],
      isNumber(surgicalBleeding.score) ? surgicalBleeding.score : null,
    ],
    [
      'Intake and output',
      intake,
      isNumber(poFluids.score) || isNumber(voided.score)
        ? (poFluids.score || 0) + (voided.score || 0)
        : null,
    ],
  ]
}

/**
 * @param {DischargeQuestionEntry[]} fields
 * @returns {DischargeScoreValue[]}
 */
const getAldreteSummaryValues = fields => {
  /** @type {DischargeScoreValue[]} */
  const values = []
  for (const field of fields) {
    values.push([field.label, '', field.score])
  }

  return values
}

/**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'tempUnit' | 'maxDuration'
 * >} param1
 * @returns {DischargeScoreSummary}
 */
export const optionSelectDischargeScoreSummary = (
  caseData,
  { appTime, tempUnit, maxDuration }
) => {
  const result = optionSelectDischargeVitalsScoreData(caseData, {
    appTime,
    tempUnit,
    maxDuration,
  })
  const fields = selectDischargeScoreFields(caseData)
  const type = selectDischargeScoreType(caseData)
  let nonVitalsComplete = true
  const totalWithoutVitals = fields.reduce((acc, field) => {
    if (field.score === null) {
      nonVitalsComplete = false
    }
    return acc + (field.score || 0)
  }, 0)
  const toReturn = {
    name: type === 'pads' ? 'PADS' : 'Aldrete',
    type,
    fields,
    complete: false,
    targetScore: type === 'pads' ? 9 : 12,
    totalScore: totalWithoutVitals,
    vitalsScore: null,
    values: null,
    vitalsComparison: null,
  }
  if (!result) {
    return toReturn
  }
  const { maxDelta, vitalsScore, vitalsComparison } = result
  /** @type {DischargeScoreValue} */
  const vitalValue = [
    'Vital signs',
    isNumber(maxDelta) ? `within ${maxDelta}% of preoperative value` : '',
    vitalsScore,
  ]
  return {
    ...toReturn,
    vitalsScore,
    complete: nonVitalsComplete && vitalsScore !== null,
    totalScore: totalWithoutVitals + vitalsScore,
    values: [
      vitalValue,
      ...(type === 'pads' ? getPadsSummaryValues : getAldreteSummaryValues)(
        fields
      ),
    ],
    vitalsComparison,
  }
}
